import React, { ReactNode, useState } from 'react';

import { faChevronDown, faChevronRight, faFolder, faFolderOpen, faPlus, faRefresh } from '@fortawesome/pro-regular-svg-icons';
import styled from 'styled-components';

import { Button, ButtonVariants } from '../Button';
import { Icon } from '../Icon';

// Types
export interface FilterCategory {
  id: string;
  label: string;
  count?: number;
  icon?: React.ComponentType | string;
  isExpandable?: boolean;
  isExpanded?: boolean;
  children?: FilterCategory[];
  customContent?: ReactNode;
}

export interface FilterSidebarProps {
  title: string;
  categories: FilterCategory[];
  selectedCategoryId?: string | null;
  onCategorySelect?: (categoryId: string | null) => void;
  onCategoryToggle?: (categoryId: string) => void;
  onRefresh?: () => void;
  onAdd?: () => void;
  showRefreshButton?: boolean;
  showAddButton?: boolean;
  refreshButtonLabel?: string;
  addButtonLabel?: string;
  isLoading?: boolean;
  emptyStateMessage?: string;
  className?: string;
  variant?: 'default' | 'compact' | 'bordered';
  maxHeight?: string;
}

// Styled Components
const SidebarContainer = styled.div<{
  variant: 'default' | 'compact' | 'bordered';
  maxHeight?: string;
}>`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.palette.white};

  ${({ variant, theme }) => {
    switch (variant) {
      case 'compact':
        return `
          padding: 0.75rem;
          gap: 0.5rem;
        `;
      case 'bordered':
        return `
          padding: 1rem;
          border: 1px solid ${theme.colors.palette.grayscale[2]};
          border-radius: 8px;
          gap: 1rem;
        `;
      default:
        return `
          padding: 1rem;
          gap: 1rem;
        `;
    }
  }}

  ${({ maxHeight }) => maxHeight && `max-height: ${maxHeight};`}
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex-shrink: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.palette.black};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled(Button)`
  padding: 0.25rem;
  min-width: auto;
  width: 28px;
  height: 28px;

  .icon-container {
    margin: 0;
  }
`;

const CategoriesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;

  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.palette.grayscale[3]} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.palette.grayscale[3]};
    border-radius: 3px;
  }
`;

const CategoryItem = styled.div<{
  level: number;
  isSelected: boolean;
  isDisabled: boolean;
}>`
  padding-left: ${({ level }) => level * 16}px;
  margin-bottom: 2px;
`;

const CategoryButton = styled.button<{
  isSelected: boolean;
  isDisabled: boolean;
}>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 4px;
  background: ${({ isSelected, theme }) => (isSelected ? theme.colors.palette.blues[0] : 'transparent')};
  color: ${({ isDisabled, theme }) => (isDisabled ? theme.colors.palette.grayscale[4] : theme.colors.palette.grayscale[7])};
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  text-align: left;
  transition: all 0.2s ease-in-out;
  opacity: ${({ isDisabled }) => (isDisabled ? 0.6 : 1)};
  font-weight: ${({ isSelected }) => (isSelected ? '600' : 'normal')};

  &:hover:not(:disabled) {
    background-color: ${({ isSelected, theme }) => (isSelected ? theme.colors.palette.blues[1] : theme.colors.palette.grayscale[1])};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`;

const CategoryIcon = styled.div`
  display: flex;
  align-items: center;
  margin-right: 0.5rem;
  font-size: 0.875rem;
  min-width: 20px;
  justify-content: center;
`;

const ExpandIcon = styled(Icon)<{ isExpanded: boolean }>`
  margin-right: 0.25rem;
  font-size: 0.75rem;
  transition: transform 0.2s ease-in-out;
  transform: ${({ isExpanded }) => (isExpanded ? 'rotate(90deg)' : 'rotate(0deg)')};
`;

const CategoryLabel = styled.span`
  flex: 1;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CategoryCount = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.palette.grayscale[5]};
  margin-left: 0.5rem;
  font-weight: normal;
`;

const CustomContent = styled.div<{ level: number }>`
  padding-left: ${({ level }) => (level + 1) * 16}px;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.palette.grayscale[5]};
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  color: ${({ theme }) => theme.colors.palette.grayscale[5]};
`;

// Component
export const FilterSidebar: React.FC<FilterSidebarProps> = ({
  title,
  categories,
  selectedCategoryId,
  onCategorySelect,
  onCategoryToggle,
  onRefresh,
  onAdd,
  showRefreshButton = true,
  showAddButton = true,
  refreshButtonLabel = 'Refresh',
  addButtonLabel = 'Add',
  isLoading = false,
  emptyStateMessage = 'No categories available',
  className,
  variant = 'default',
  maxHeight,
}) => {
  const [localExpandedIds, setLocalExpandedIds] = useState<Set<string>>(new Set());

  const handleCategoryClick = (categoryId: string, category: FilterCategory) => {
    if (category.isExpandable && category.children?.length) {
      handleToggleExpansion(categoryId);
    }

    if (onCategorySelect) {
      const newSelection = selectedCategoryId === categoryId ? null : categoryId;
      onCategorySelect(newSelection);
    }
  };

  const handleToggleExpansion = (categoryId: string) => {
    if (onCategoryToggle) {
      onCategoryToggle(categoryId);
    } else {
      setLocalExpandedIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(categoryId)) {
          newSet.delete(categoryId);
        } else {
          newSet.add(categoryId);
        }
        return newSet;
      });
    }
  };

  const isExpanded = (category: FilterCategory): boolean => {
    return category.isExpanded ?? localExpandedIds.has(category.id);
  };

  const renderCategory = (category: FilterCategory, level = 0): ReactNode => {
    const isSelected = selectedCategoryId === category.id;
    const isDisabled = category.count !== undefined && category.count === 0;
    const expanded = isExpanded(category);
    const hasChildren = category.children && category.children.length > 0;

    return (
      <React.Fragment key={category.id}>
        <CategoryItem level={level} isSelected={isSelected} isDisabled={isDisabled}>
          <CategoryButton
            isSelected={isSelected}
            isDisabled={isDisabled}
            onClick={() => handleCategoryClick(category.id, category)}
            disabled={isDisabled}
            title={`${category.label}${category.count !== undefined ? ` (${category.count})` : ''}`}
          >
            {category.isExpandable && hasChildren && <ExpandIcon icon={expanded ? faChevronDown : faChevronRight} isExpanded={expanded} />}

            <CategoryIcon>
              {category.isExpandable ? <Icon icon={expanded && hasChildren ? faFolderOpen : faFolder} /> : <span style={{ width: '16px' }} />}
            </CategoryIcon>

            <CategoryLabel>{category.label}</CategoryLabel>

            {category.count !== undefined && <CategoryCount>({category.count})</CategoryCount>}
          </CategoryButton>

          {category.customContent && <CustomContent level={level}>{category.customContent}</CustomContent>}
        </CategoryItem>

        {expanded && hasChildren && category.children?.map((child) => renderCategory(child, level + 1))}
      </React.Fragment>
    );
  };

  return (
    <SidebarContainer variant={variant} maxHeight={maxHeight} className={className}>
      <HeaderSection>
        <TitleRow>
          <Title>{title}</Title>
          <ActionButtons>
            {showRefreshButton && onRefresh && (
              <ActionButton variant={ButtonVariants.SECONDARY} onClick={onRefresh} disabled={isLoading} title={refreshButtonLabel}>
                <Icon icon={faRefresh} />
              </ActionButton>
            )}
            {showAddButton && onAdd && (
              <ActionButton variant={ButtonVariants.SECONDARY} onClick={onAdd} title={addButtonLabel}>
                <Icon icon={faPlus} />
              </ActionButton>
            )}
          </ActionButtons>
        </TitleRow>
      </HeaderSection>

      <CategoriesContainer>
        {isLoading ? (
          <LoadingState>Loading...</LoadingState>
        ) : categories.length === 0 ? (
          <EmptyState>{emptyStateMessage}</EmptyState>
        ) : (
          categories.map((category) => renderCategory(category))
        )}
      </CategoriesContainer>
    </SidebarContainer>
  );
};

FilterSidebar.displayName = 'FilterSidebar';
